import React from "react";
import { MessageSquare, ArrowRight, Calendar, Bot, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { StatusDropdown } from "@/components/ui/status-dropdown";
import { ChatMessages } from "./ChatMessages";
import { Conversation, Message } from "@/types/inbox.types";
import { format } from "date-fns";
import { ConversationChatSkeleton } from "@/components/ui/skeleton";

interface ConversationDetailsPanelProps {
  selectedConversationId: string | null;
  loadingConversation: boolean;
  conversationInfo: Conversation | null;
  conversationMessages: Message[];
  onBackClick: () => void;
  onStatusChange: (newStatus: any) => void;
}

export const ConversationDetailsPanel = ({
  selectedConversationId,
  loadingConversation,
  conversationInfo,
  conversationMessages,
  onBackClick,
  onStatusChange,
}: ConversationDetailsPanelProps) => {
  return (
    <div
      className={`${
        selectedConversationId
          ? "w-full md:w-[45%]"
          : "hidden md:flex md:w-[45%]"
      } flex-col bg-white relative`}
    >
      {!selectedConversationId ? (
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div>
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-indigo-300" />
            <h3 className="text-lg font-medium mb-1 text-slate-800">
              Select a conversation
            </h3>
            <p className="text-slate-500 text-sm max-w-md">
              Choose a conversation from the list to view the details
            </p>
          </div>
        </div>
      ) : loadingConversation ? (
        <ConversationChatSkeleton />
      ) : (
        <div className="flex flex-col h-full relative">
          {/* Conversation header with back button on mobile */}
          <div className="p-4 border-b bg-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Back button for mobile */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackClick}
                className="md:hidden p-2"
              >
                <ArrowRight className="h-4 w-4 rotate-180" />
              </Button>

              <div>
                <div className="flex items-center">
                  <h3 className="text-xl font-bold mb-1 text-slate-900">
                    {conversationInfo?.user_name || "User"}
                  </h3>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(
                      new Date(conversationInfo?.created_at || new Date()),
                      "MMM d, yyyy",
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bot className="h-3 w-3" />
                    {conversationInfo?.agent_name || "Agent"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Status Dropdown */}
              {conversationInfo?.id && (
                <StatusDropdown
                  conversationId={conversationInfo.id}
                  currentStatus={conversationInfo.status}
                  onStatusChange={onStatusChange}
                  size="sm"
                />
              )}

              {/* Only show state if conversation is not completed */}
              {conversationInfo?.state !== "completed" && (
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-amber-100 text-amber-800">
                  in progress
                </span>
              )}

              {/* Details toggle for mobile */}
              <Button
                variant="outline"
                size="sm"
                className="md:hidden"
                onClick={() => {
                  // Toggle details panel on mobile - you can implement this state
                }}
              >
                <Info className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages section with scrollable container */}
          <div className="flex-1 min-h-0 relative">
            <ChatMessages
              messages={conversationMessages}
              isLoading={loadingConversation}
              agentName={conversationInfo?.agent_name}
              agentAvatar={conversationInfo?.avatar_url}
              brandColor="#060520"
            />
          </div>
        </div>
      )}
    </div>
  );
};
